<template>
  <b-form
    class="reset-password"
    novalidate
    @submit.prevent="submit"
  >

    <div>
      Changement de mot de passe
    </div>

    <div class="reset-password__enter-email">
      Vous avez demandé à réinitialiser le mot de passe de votre compte Hamac
    </div>

    <!-- New password -->
    <div
      :class="[
        'form-group',
        hasErrorFor('password') ? 'form-group--error' : ''
      ]"
    >
      <div class="form-group__label">
        <ui-label for="password">Nouveau mot de passe</ui-label>
      </div>
      <div class="form-group__field">
        <ui-form-inner-button-icon
          :name="showPassword ? 'visibility_off' : 'visibility'"
          :variant="UI.VARIANT.DARK"
          :elevation="UI.ELEVATION.LEVEL_0"
          :size="UI.SIZE.LARGE"
          tabindex="-1"
          placement="right"
          ripple
          @click="togglePasswordVisibility"
        >
          <ui-form-input
            v-model="password"
            v-focus
            :size="UI.SIZE.LARGE"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            data-cy="password"
          />
        </ui-form-inner-button-icon>
        <div
          v-if="hasErrorFor('password')"
          class="form-group__error"
        >
          {{ $t('validation.password') }}
        </div>
      </div>
    </div>

    <div class="reset-password__requirements">
      <div>
        <p>
          1 minuscule
          <transition
            enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
            leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
          >
            <ui-icon
              v-if="satisfiedConstraints.lowercase"
              name="check"
              class="reset-password__check-icon"
            />
          </transition>
        </p>
      </div>
      <div>
        8 caractères minimum
        <transition
          enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
          leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
        >
          <ui-icon
            v-if="satisfiedConstraints.length"
            name="check"
            class="reset-password__check-icon"
          />
        </transition>
      </div>
      <div>
        1 majuscule
        <transition
          enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
          leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
        >
          <ui-icon
            v-if="satisfiedConstraints.uppercase"
            name="check"
            class="reset-password__check-icon"
          />
        </transition>
      </div>
      <div>
        1 chiffre
        <transition
          enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
          leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
        >
          <ui-icon
            v-if="satisfiedConstraints.digit"
            name="check"
            class="reset-password__check-icon"
          />
        </transition>
      </div>
    </div>

    <!-- New password -->
    <div
      :class="[
        'form-group',
        hasErrorFor('passwordConfirmation') ? 'form-group--error' : ''
      ]"
    >
      <div class="form-group__label">
        <ui-label for="passwordConfirmation">Confirmer le mot de passe</ui-label>
      </div>
      <div class="form-group__field">
        <ui-form-inner-button-icon
          :name="showPassword ? 'visibility_off' : 'visibility'"
          :variant="UI.VARIANT.DARK"
          :size="UI.SIZE.LARGE"
          :elevation="UI.ELEVATION.LEVEL_0"
          tabindex="-1"
          placement="right"
          ripple
          @click="togglePasswordVisibility"
        >
          <ui-form-input
            v-model="passwordConfirmation"
            :size="UI.SIZE.LARGE"
            :type="showPassword ? 'text' : 'password'"
            name="passwordConfirmation"
            data-cy="password-confirmation"
          />
        </ui-form-inner-button-icon>
        <div
          v-if="hasErrorFor('passwordConfirmation')"
          class="form-group__error"
        >
          {{ $t('validation.password_confirmation') }}
        </div>
      </div>
    </div>

    <!-- Success caveat -->
     <transition
      v-if="!hasRemoteError && resetSuccess"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <ui-caveat
        :variant="UI.VARIANT.LIGHT"
        :aside-variant="UI.VARIANT.SUCCESS"
        :size="UI.SIZE.LARGE"
        class="reset-password__success-caveat"
      >
        <template #aside>
          <ui-icon name="task_alt" />
        </template>
        <template #default>
          <p>Votre mot de passe a été changé, vous allez être redirigé sur la page de connexion dans quelques secondes</p>
        </template>
      </ui-caveat>
    </transition>

    <transition
      v-else-if="hasRemoteError"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <ui-caveat
        :variant="UI.VARIANT.LIGHT"
        :aside-variant="UI.VARIANT.DANGER"
        :size="UI.SIZE.LARGE"
        class="reset-password__success-caveat"
      >
        <template #aside>
          <ui-icon name="error" />
        </template>
        <template #default>
          <p>{{ remoteErrorMessage }}</p>
        </template>
      </ui-caveat>
    </transition>

    <!-- Submit -->
    <ui-button
      :status="changePasswordStatus"
      :size="UI.SIZE.LARGE"
      :variant="UI.VARIANT.PRIMARY"
      type="submit"
      data-cy="submit"
      class="reset-password__submit"
      pill
    >
      Je change le mot de passe
    </ui-button>

  </b-form>
</template>

<style lang="sass" scoped>
.reset-password

  .form-group .form-group__label label
    text-transform: uppercase
    font-weight: $font-weight-bold
    font-size: 100%
    letter-spacing: 0.4px

  .form-group

    .ui-form-input.form-control
      border-radius: $border-radius-pill
      border-color: $wine-berry
      color: $wine-berry
      font-family: $font-family-monospace
      letter-spacing: 0.5px

      &:focus
        box-shadow: 0 0 0 0.2rem mix-alpha($wine-berry, 25%)

  .form-group
    &__field
      margin-bottom: space-x(4)

  .form-group.form-group--error

    .ui-form-input.form-control
      border-color: $danger
      box-shadow: 0 0 0 0.2rem mix-alpha($danger, 20%)

      &:focus
        box-shadow: 0 0 0 0.2rem mix-alpha($danger, 60%)

  &__enter-email
    color: $gray-deep
    font-weight: $font-weight-light
    margin-bottom: space-x(2.5)

  &__success-caveat
    margin-top: space-x(2.5)
    margin-bottom: space-x(2.5)
    animation: fadeIn 0.5s

  &__submit
    @include button-variant($wine-berry, $wine-berry)

    text-transform: uppercase
    font-weight: $font-weight-normal
    font-family: $font-family-heading
    font-size: 105%
    line-height: 1.8
    letter-spacing: .4px
    word-spacing: 4px
    border-radius: $border-radius-pill
    margin-bottom: space-x(2.5)
    display: block
    width: 100%

  &__requirements
    display: grid
    position: relative
    grid-template-columns: repeat(2, 1fr)
    grid-template-rows: repeat(2, 1fr)
    column-gap: space-x(4)
    row-gap: $form-group-margin-bottom * .5
    margin-bottom: $form-group-margin-bottom

    div
      display: flex
      flex-direction: row
      align-items: center
      gap: space-x(1)
      font-weight: $font-weight-light

  &__check-icon
    color: $success
    font-size: 120%

</style>

<script lang="ts">
import { useLinkHandler } from '~/composables/core/link-handler'
import { useValidation } from '~/composables/core/validation'
import Console from '~/utils/lib/core/console'
import { __, debounce, delay, isEmpty, length, merge, sustainedPromise, test, toString } from '~/utils/lib/core/prelude'
import passwordConstraints from '~/utils/lib/core/validation/password'
import { useStore_account_authentication } from '~/stores/account/authentication'

const console = new Console('component:page-restore')

const constraints = {
  password: merge(passwordConstraints, { presence: true }),
  passwordConfirmation: {
    presence: true,
    equality: 'password'
  },
}

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      layout: 'authentication',
      pageTransition: {
        enterActiveClass: 'animate__animated animate__fadeIn ease-in-out a3',
        leaveActiveClass: 'd-none',
      },
    })
    useHead({
      title: 'Récupération de mot de passe — Hamac',
    })

    const store_authentication = useStore_account_authentication()

    return {
      ...useLinkHandler(),
      ...useValidation(constraints),
      store_authentication,
    }
  },
  data() {
    return {
      isPending: false,
      hasRemoteError: false,
      resetSuccess: false,
      remoteErrorMessage: '',
      password: '',
      passwordConfirmation: '',
      showPassword: false,
      satisfiedConstraints: {
        length: false,
        digit: false,
        uppercase: false,
        lowercase: false
      },
      debouncedPasswordChecking: null as unknown as Function,
    }
  },
  computed: {
    changePasswordStatus() {
      if (this.isPending) return this.UI.STATUS.DEFFERED
      return this.UI.STATUS.ENABLED
    }
  },
  watch: {
    // Allows the user to visibly see which constrainst are
    // respected, needs to be edited if the constraints change
    password(val) {
      this.debouncedPasswordChecking(val)
    }
  },
  created() {
    // Debounced password constraints checker
    this.debouncedPasswordChecking = debounce(
      500,
      (password) => this.checkPasswordConstraints(password)
    )
  },
  methods: {
    checkPasswordConstraints(val) {
      const satisfiedConstraints = {
        length: false,
        digit: false,
        uppercase: false,
        lowercase: false
      }

      if (length(val) > 7) satisfiedConstraints.length = true
      if (test(/(?=.*\d)/, val)) satisfiedConstraints.digit = true
      if (test(/(?=.*[a-z])/, val)) satisfiedConstraints.lowercase = true
      if (test(/(?=.*[A-Z])/, val)) satisfiedConstraints.uppercase = true

      this.satisfiedConstraints = satisfiedConstraints
    },
    submit() {
      if (this.isPending)
        return

      this.checkAll()

      if (this.hasError())
        return

      this.isPending = true

      const token = this.$route.query.token as string

      return sustainedPromise(
          250,
          this.store_authentication.changePassword(
            { password: this.password, token }
          ),
        )
        .then(async ({ url }) => {
          this.resetSuccess = true
          this.hasRemoteError = false
          this.remoteErrorMessage = ''

          // Delay to auto redirects the user after a successful
          // password change, the change password screen has
          // no back button so this is a sort of back button
          await delay(4500)

          if (!isEmpty(url)) {
            console.log('user needs to be redirected on dispatch')
            // if the micro-service returns back a route it means that the user
            // made a demand on the dispatch page, redirect the user to the
            // dispatch page with the route in the url
            this.goToRoute(`/account/dispatch?url=${url}`)
          } else {
            this.goToRoute('/account/authentication')
          }

          return { ok: true }
        })
        .catch((err) => {
          this.remoteErrorMessage = toString(err.message)
          this.hasRemoteError = true
          this.isPending = false

          return { ok: false }
        })
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword
    },
  },
})
</script>
